/**
 * Physical-continent convention for clip masks (CL-237).
 *
 * Natural Earth assigns each COUNTRY to one continent, and it files all of
 * Russia under Europe. That is fine for attribute work ("population of
 * European countries") but wrong for PHYSICAL masks: a "DEM of Asia" without
 * Siberia is not the Asia on any physical wall map, and a "DEM of Europe"
 * cannot honestly paint terrain to the Pacific. The classroom convention is
 * the Ural boundary: European Russia belongs to Europe, Siberia to Asia.
 *
 * This module implements that convention for the raster/clip mask path ONLY
 * (resolveRegion's continent branch): Russia's polygon is split at the Ural
 * meridian (60°E) — the west part replaces Russia in the Europe mask, the
 * east part is appended to the Asia mask. Everything else passes through
 * untouched, and callers that want country-level membership keep using
 * filterByContinent directly.
 *
 * Known approximation: a straight meridian stands in for the true Ural
 * River–Ural Mountains line, and it bisects Novaya Zemlya (52–69°E). At
 * continent-DEM resolution the difference is invisible; documented so nobody
 * "fixes" it into a surprise. Franz Josef Land (80–82°N) sits above the
 * Europe COG's north bound and simply doesn't paint.
 *
 * All functions are pure and unit tested.
 */

/** The Europe/Asia split meridian (°E) — the classroom Ural line. */
export const URAL_MERIDIAN = 60

const isFiniteNum = (v) => typeof v === 'number' && Number.isFinite(v)

/**
 * Clip one linear ring against the half-plane lon < meridian (keepWest=true)
 * or lon > meridian (keepWest=false). Sutherland–Hodgman against a single
 * vertical line: points on the inside are kept, crossings insert the
 * intersection with the meridian. Returns a closed ring, or null when fewer
 * than 3 distinct vertices survive. Pure.
 * @param {number[][]} ring [[lng,lat], …] (closed or open — output is closed)
 */
export function clipRingAtMeridian(ring, meridian, keepWest) {
  if (!Array.isArray(ring) || ring.length < 3) return null
  const inside = (p) => (keepWest ? p[0] <= meridian : p[0] >= meridian)
  const cross = (a, b) => {
    const t = (meridian - a[0]) / (b[0] - a[0])
    return [meridian, a[1] + t * (b[1] - a[1])]
  }

  // Work on an OPEN ring (drop a duplicated closing vertex if present).
  const src = ring.slice()
  const first = src[0]
  const last = src[src.length - 1]
  if (first && last && first[0] === last[0] && first[1] === last[1]) src.pop()

  const out = []
  for (let i = 0; i < src.length; i++) {
    const a = src[i]
    const b = src[(i + 1) % src.length]
    if (!isFiniteNum(a?.[0]) || !isFiniteNum(a?.[1])) return null
    const aIn = inside(a)
    const bIn = inside(b)
    if (aIn) out.push(a)
    if (aIn !== bIn && a[0] !== b[0]) out.push(cross(a, b))
  }
  if (out.length < 3) return null
  out.push([out[0][0], out[0][1]]) // close
  return out
}

/**
 * Split a Polygon/MultiPolygon feature at a meridian into { west, east }
 * feature halves (either may be null when no area falls on that side).
 * Holes are clipped alongside their outer rings; a polygon whose outer ring
 * vanishes on a side is dropped from that side. Properties are shared by
 * reference (masks only read geometry). Pure.
 */
export function splitFeatureAtMeridian(feature, meridian = URAL_MERIDIAN) {
  const geom = feature?.geometry
  if (!geom) return { west: null, east: null }
  const polys =
    geom.type === 'Polygon'
      ? [geom.coordinates]
      : geom.type === 'MultiPolygon'
        ? geom.coordinates
        : null
  if (!polys) return { west: null, east: null }

  const halves = { west: [], east: [] }
  for (const rings of polys) {
    if (!Array.isArray(rings) || !rings.length) continue
    for (const [side, keepWest] of [
      ['west', true],
      ['east', false],
    ]) {
      const outer = clipRingAtMeridian(rings[0], meridian, keepWest)
      if (!outer) continue
      const clipped = [outer]
      for (let h = 1; h < rings.length; h++) {
        const hole = clipRingAtMeridian(rings[h], meridian, keepWest)
        if (hole) clipped.push(hole)
      }
      halves[side].push(clipped)
    }
  }

  const toFeature = (coords) =>
    coords.length
      ? {
          type: 'Feature',
          properties: feature.properties || {},
          geometry: { type: 'MultiPolygon', coordinates: coords },
        }
      : null
  return { west: toFeature(halves.west), east: toFeature(halves.east) }
}

/** Is this Natural Earth feature Russia? Checks the ISO fields NE actually
 *  uses (ISO_A3 can be '-99' on some sovereigns; ADM0_A3 is the fallback),
 *  then the display name. Pure — exported for tests. */
export function isRussiaFeature(feature) {
  const p = feature?.properties || {}
  const iso = String(p.iso3 || p.ISO_A3 || p.iso_a3 || '').toUpperCase()
  const adm = String(p.ADM0_A3 || p.adm0_a3 || '').toUpperCase()
  if (iso === 'RUS' || (iso === '-99' && adm === 'RUS') || (!iso && adm === 'RUS')) return true
  const name = String(p.name_en || p.NAME || p.name || '').toLowerCase()
  return name === 'russia' || name === 'russian federation'
}

/**
 * Apply the physical-continent convention to a continent mask:
 *  - 'europe': Russia's feature (present — NE files Russia under Europe) is
 *    replaced by its west-of-Urals half.
 *  - 'asia': Russia's east-of-Urals half (from the full countries FC) is
 *    appended.
 *  - any other continent/subregion: returned unchanged.
 * Never mutates its inputs; falls back to the unmodified mask if Russia
 * can't be found or split (a wrong-but-rendered mask beats a crash). Pure.
 *
 * @param {object} fc continent mask FeatureCollection (from filterByContinent)
 * @param {string} continentName spoken continent name (any case)
 * @param {object} allCountries the FULL Natural Earth countries FC
 */
export function applyPhysicalContinentConvention(fc, continentName, allCountries) {
  const cname = String(continentName || '')
    .trim()
    .toLowerCase()
  if (cname !== 'europe' && cname !== 'asia') return fc
  if (!fc?.features?.length) return fc

  try {
    if (cname === 'europe') {
      const features = fc.features.map((f) => {
        if (!isRussiaFeature(f)) return f
        const { west } = splitFeatureAtMeridian(f, URAL_MERIDIAN)
        return west // null drops below when Russia is entirely east (impossible, but safe)
      })
      return { type: 'FeatureCollection', features: features.filter(Boolean) }
    }
    // asia
    const russia = (allCountries?.features || []).find(isRussiaFeature)
    if (!russia) return fc
    const { east } = splitFeatureAtMeridian(russia, URAL_MERIDIAN)
    if (!east) return fc
    return { type: 'FeatureCollection', features: [...fc.features, east] }
  } catch {
    return fc
  }
}
